import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import API_BASE_URL from "../Config";
import Header from "./Header";
import Footer from "./Footer";
import "./styles/OrderList.css";
import "../styles/global.css";

/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}>{d}</svg>
);

const SearchIcon    = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />;
const EyeIcon       = (p) => <Icon {...p} d={<><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></>} />;
const TruckIcon     = (p) => <Icon {...p} d={<><rect x="1" y="3" width="15" height="13" rx="1" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></>} />;
const CloseIcon     = (p) => <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />;
const ShoppingBagIcon=(p) => <Icon {...p} d={<><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></>} />;
const ChevronLeftIcon=(p) => <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />;
const ChevronRightIcon=(p)=> <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />;
const DownloadIcon  = (p) => <Icon {...p} d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>} />;
const UserIcon      = (p) => <Icon {...p} d={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>} />;
const MapPinIcon    = (p) => <Icon {...p} d={<><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>} />;
const ReceiptIcon   = (p) => <Icon {...p} d={<><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z" /><path d="M14 8H8M16 12H8M13 16H8" /></>} />;
const XCircleIcon   = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6M9 9l6 6" /></>} />;
const CheckIcon     = (p) => <Icon {...p} d={<><path d="M20 6 9 17l-5-5" /></>} />;
const TagIcon       = (p) => <Icon {...p} d={<><path d="M12 2H2v10l9.29 9.29a1 1 0 0 0 1.41 0l7.59-7.59a1 1 0 0 0 0-1.41L12 2z" /><path d="M7 7h.01" /></>} />;

/* ────────── Multi-Select ────────── */
const MultiSelect = ({ options, value, onChange, placeholder = "Filter…" }) => {
  const [open, setOpen] = useState(false);
  const toggle = (opt) => onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  return (
    <div className="ol-multiselect" style={{ position: "relative" }}>
      <div className="ol-multiselect__trigger" onClick={() => setOpen(!open)} tabIndex={0}
        onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
        <span className="ol-multiselect__label">
          {value.length === 0
            ? <span style={{ color: "#9aa8a2" }}>{placeholder}</span>
            : value.map(v => <span key={v} className="ol-multiselect__tag">{v}</span>)}
        </span>
        <span className="ol-multiselect__caret">
          {value.length > 0 && (
            <button className="ol-multiselect__clear" onClick={e => { e.stopPropagation(); onChange([]); }} tabIndex={-1} type="button">
              <XCircleIcon size={14} />
            </button>
          )}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s ease" }}>
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </div>
      {open && (
        <div className="ol-multiselect__dropdown" role="listbox">
          {options.map(opt => (
            <div key={opt} className={`ol-multiselect__option ${value.includes(opt) ? "is-selected" : ""}`}
              role="option" onClick={() => toggle(opt)} tabIndex={0}
              onKeyDown={e => e.key === "Enter" && toggle(opt)}>
              <span className="ol-multiselect__check">{value.includes(opt) && <CheckIcon size={12} stroke={2.5} />}</span>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach(i => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  return (
    <div className="ol-pagination">
      <div className="ol-pagination__info">
        <span>Showing <b>{Math.min((currentPage - 1) * perPage + 1, totalRows)}</b>–
          <b>{Math.min(currentPage * perPage, totalRows)}</b> of <b>{totalRows}</b></span>
        <select className="ol-pagination__perpage" value={perPage} onChange={e => onPerPageChange(Number(e.target.value))}>
          {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>
      <div className="ol-pagination__pages">
        <button className="ol-pagination__btn" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}>
          <ChevronLeftIcon size={16} />
        </button>
        {pages.map((p, i) =>
          p === "..." ? <span key={`e-${i}`} className="ol-pagination__ellipsis">…</span> : (
            <button key={p} className={`ol-pagination__btn ${p === currentPage ? "is-active" : ""}`} onClick={() => onPageChange(p)}>{p}</button>
          )
        )}
        <button className="ol-pagination__btn" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)}>
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── PDF Download Helper ────────── */
const downloadOrderPDF = (order) => {
  const products = Object.values(order.order_products || {});
  const subtotal = products.reduce((sum, p) => {
    const disc = p.price - (p.price * p.dprice) / 100;
    return sum + disc * p.qty;
  }, 0);

  const deliveryCharges = Number(order.shipping_amount || 0);
  const couponAmount    = Number(order.coupen_amount   || 0);
  const couponCode      = order.coupon_code || "";
  const finalAmount     = order.final_amount
    ? Number(order.final_amount)
    : Number(order.totalamount);

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Order ${order.orderid}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 32px; color: #1f2a37; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; border-bottom: 2px solid #10b981; padding-bottom: 16px; }
    .brand { font-size: 22px; font-weight: 800; color: #10b981; }
    .brand span { color: #0f766e; }
    .invoice-label { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #6b7a74; letter-spacing: .06em; }
    .invoice-id { font-size: 20px; font-weight: 800; color: #1f2a37; }
    .meta { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; margin-bottom: 28px; }
    .meta-block { background: #f0fbf6; border: 1px solid #d6efe2; border-radius: 10px; padding: 14px 16px; }
    .meta-block h4 { font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: #6b7a74; margin: 0 0 6px 0; font-weight: 700; }
    .meta-block p { margin: 0; font-size: 13px; line-height: 1.6; color: #1f2a37; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; }
    .status-paid   { background: #ecfdf5; color: #047857; border: 1px solid #b7e8d2; }
    .status-unpaid { background: #fff1f0; color: #c0392b; border: 1px solid #fbd7d3; }
    table { width: 100%; border-collapse: collapse; border-radius: 10px; overflow: hidden; margin-bottom: 24px; }
    thead tr { background: #f0fbf6; }
    th { padding: 12px 14px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #5b6b66; border-bottom: 1px solid #d6efe2; text-align: left; }
    th:last-child { text-align: right; }
    td { padding: 13px 14px; font-size: 13px; border-bottom: 1px solid #eef4f1; vertical-align: middle; }
    td:last-child { text-align: right; font-weight: 600; }
    .product-img { width: 44px; height: 44px; object-fit: cover; border-radius: 8px; border: 1px solid #e3f0e9; }
    .total-row { display: flex; justify-content: flex-end; margin-bottom: 32px; }
    .total-box { background: #f0fbf6; border: 1px solid #d6efe2; border-radius: 12px; padding: 18px 24px; min-width: 280px; }
    .total-line { display: flex; justify-content: space-between; align-items: center; font-size: 14px; color: #6b7a74; padding: 5px 0; }
    .total-line.coupon { color: #16a34a; font-weight: 600; }
    .total-line.grand { font-size: 16px; font-weight: 700; color: #1f2a37; border-top: 1px solid #d6efe2; margin-top: 8px; padding-top: 12px; }
    .total-line.grand span:last-child { color: #059669; }
    .footer { text-align: center; font-size: 12px; color: #9aa8a2; border-top: 1px solid #eef4f1; padding-top: 16px; margin-top: 24px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">Order<span>Invoice</span></div>
    <div style="text-align:right">
      <div class="invoice-label">Invoice</div>
      <div class="invoice-id">#${order.orderid}</div>
      <div style="font-size:13px;color:#6b7a74;margin-top:4px">${order.orderdate || ""}</div>
    </div>
  </div>
  <div class="meta">
    <div class="meta-block">
      <h4>Bill To</h4>
      <p><strong>${order.name}</strong><br/>${order.email}</p>
    </div>
    <div class="meta-block">
      <h4>Ship To</h4>
      <p>${order.address || ""}, ${order.city || ""}<br/>${order.state || ""}, ${order.country || ""}</p>
    </div>
    <div class="meta-block">
      <h4>Payment Status</h4>
      <span class="status-badge ${order.status === "Paid" ? "status-paid" : "status-unpaid"}">${order.status}</span>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Product</th><th>Price</th><th>Discount</th><th>Qty</th><th style="text-align:right">Total</th>
      </tr>
    </thead>
    <tbody>
      ${products.map(p => {
        const lineTotal = (p.price - (p.price * p.dprice) / 100) * p.qty;
        return `<tr>
          <td>
            <div style="display:flex;align-items:center;gap:12px">
              <img src="${p.image}" alt="${p.name}" class="product-img" onerror="this.style.display='none'"/>
              <span>${p.name}</span>
            </div>
          </td>
          <td>₹${p.price}</td>
          <td>${p.dprice}%</td>
          <td>${p.qty}</td>
          <td style="text-align:right;font-weight:600">₹${lineTotal.toFixed(2)}</td>
        </tr>`;
      }).join("")}
    </tbody>
  </table>
  <div class="total-row">
    <div class="total-box">
      <div class="total-line">
        <span>Subtotal (MRP)</span>
        <span style="font-weight:600;color:#1f2a37">₹${subtotal.toFixed(2)}</span>
      </div>
      <div class="total-line">
        <span>Delivery Charges</span>
        <span style="font-weight:600;color:${deliveryCharges === 0 ? "#16a34a" : "#1f2a37"}">
          ${deliveryCharges === 0 ? "FREE" : "₹" + deliveryCharges.toFixed(2)}
        </span>
      </div>
      ${couponAmount > 0 ? `
      <div class="total-line coupon">
        <span>Coupon Discount${couponCode ? " (" + couponCode + ")" : ""}</span>
        <span>- ₹${couponAmount.toFixed(2)}</span>
      </div>` : ""}
      <div class="total-line grand">
        <span>Amount Paid</span>
        <span>₹${finalAmount.toFixed(2)}</span>
      </div>
    </div>
  </div>
  <div class="footer">Thank you for your order. This is a computer-generated invoice.</div>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url  = URL.createObjectURL(blob);
  const win  = window.open(url, "_blank");
  if (win) {
    win.onload = () => {
      win.print();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    };
  }
};

/* ────────── Main Component ────────── */
const OrderList = () => {
  const navigate = useNavigate();
  const [orders, setOrders]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showModal, setShowModal]         = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterText, setFilterText]       = useState("");
  const [statusFilter, setStatusFilter]   = useState([]);
  const [currentPage, setCurrentPage]     = useState(1);
  const [perPage, setPerPage]             = useState(10);
  const [totalRows, setTotalRows]         = useState(0);

  const token = localStorage.getItem("authToken");

  // ─── Guards against a race condition: if a debounced search request
  // and a manual pagination click both fire close together, whichever
  // RESPONSE arrives last used to win — even if it was the older/stale
  // request. That could snap the page back to 1 right after clicking
  // page 2, or show outdated results after a fresh search. This ref
  // tags every outgoing request with an ID; only the response matching
  // the CURRENT (latest) request is ever applied to state. ───
  const fetchIdRef = useRef(0);

  const fetchOrders = (pg = 1, size = perPage, search = filterText, status = statusFilter) => {
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    const params = new URLSearchParams({ page: pg, per_page: size });
    if (search) params.append("search", search);
    if (status.length > 0) params.append("status", status.join(","));
    fetch(`${API_BASE_URL}api/orders?${params.toString()}`, {
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (requestId !== fetchIdRef.current) return; // a newer request has since been sent — ignore this stale response
        if (data.status) {
          setOrders(data.data.data);
          setTotalRows(data.data.total);
          setCurrentPage(data.data.current_page);
        }
      })
      .catch(err => console.error("Error fetching orders:", err))
      .finally(() => {
        if (requestId === fetchIdRef.current) setLoading(false);
      });
  };

  useEffect(() => { fetchOrders(1, perPage); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced: typing a search term or changing the status filter
  // re-queries the backend from page 1, instead of only re-filtering
  // whatever happened to already be loaded. Skips the very first mount
  // so it doesn't fire a redundant duplicate of the effect above.
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) { isFirstRun.current = false; return; }
    const t = setTimeout(() => { fetchOrders(1, perPage, filterText, statusFilter); }, 350);
    return () => clearTimeout(t);
  }, [filterText, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety net: re-applies the same match locally on whatever came back.
  // If the backend already filtered by search/status, this is a no-op.
  // If a given deployment's /api/orders doesn't support these params yet,
  // this keeps the old (page-only) filtering behavior as a fallback
  // instead of silently showing unfiltered results.
  const filteredOrders = useMemo(() => {
    let result = orders;
    if (filterText) {
      result = result.filter(o =>
        o.orderid?.toString().toLowerCase().includes(filterText.toLowerCase()) ||
        o.name?.toLowerCase().includes(filterText.toLowerCase()) ||
        o.email?.toLowerCase().includes(filterText.toLowerCase())
      );
    }
    if (statusFilter.length > 0) result = result.filter(o => statusFilter.includes(o.status));
    return result;
  }, [filterText, statusFilter, orders]);

  const handlePageChange    = (p)    => { setCurrentPage(p); fetchOrders(p, perPage, filterText, statusFilter); };
  const handlePerPageChange = (size) => { setPerPage(size); setCurrentPage(1); fetchOrders(1, size, filterText, statusFilter); };
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const handleView  = (order) => { setSelectedOrder(order); setShowModal(true); };
  const handleClose = ()      => setShowModal(false);
  const handleTrack = (id)    => navigate(`/trackorder/${id}`);

  /* ── Columns ── */
  const columns = useMemo(() => [
    {
      name: "Order ID",
      selector: r => r.orderid,
      cell: r => <span className="ol-orderid-pill">#{r.orderid}</span>,
      sortable: true, width: "150px",
    },
    {
      name: "Customer",
      selector: r => r.name,
      cell: r => (
        <div className="ol-customer-cell">
          <span className="ol-customer-name">{r.name}</span>
          <span className="ol-customer-email">{r.email}</span>
        </div>
      ),
      sortable: true, grow: 2,
    },
    {
      name: "Mobile",
      selector: r => r.mobile,
      cell: r => <span className="ol-cell-muted">{r.mobile}</span>,
      width: "140px",
    },
    {
      name: "Order Date",
      selector: r => r.orderdate,
      cell: r => <span className="ol-cell-muted">{r.orderdate}</span>,
      sortable: true, width: "140px",
    },
    {
      name: "Total (MRP)",
      selector: r => r.totalamount,
      cell: r => <span className="ol-amount-cell">₹{r.totalamount}</span>,
      sortable: true, width: "130px",
    },
    {
      name: "Coupon",
      selector: r => r.coupon_code,
      cell: r => r.coupon_code
        ? (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            background: "#ecfdf5", color: "#047857",
            border: "1px solid #b7e8d2",
            borderRadius: 999, padding: "3px 9px",
            fontSize: 12, fontWeight: 700,
          }}>
            <TagIcon size={11} /> {r.coupon_code}
          </span>
        )
        : <span className="ol-cell-muted">—</span>,
      width: "130px",
    },
    {
      name: "Amount Paid",
      selector: r => r.final_amount || r.totalamount,
      cell: r => (
        <span className="ol-amount-cell" style={{ color: "#059669" }}>
          ₹{r.final_amount
            ? Number(r.final_amount).toFixed(2)
            : Number(r.totalamount).toFixed(2)}
        </span>
      ),
      sortable: true, width: "130px",
    },
    {
      name: "Status",
      selector: r => r.status,
      cell: r => (
        <span className={`ol-status-pill ${r.status === "Paid" ? "is-paid" : r.status === "Un Paid" ? "is-unpaid" : "is-other"}`}>
          <span className="dot" />{r.status}
        </span>
      ),
      sortable: true, width: "130px",
    },
    {
      name: "Actions",
      cell: r => (
        <div className="ol-row-actions">
          <button className="ol-ico-btn view" onClick={() => handleView(r)} title="View Order"><EyeIcon size={15} /></button>
          <button className="ol-ico-btn track" onClick={() => handleTrack(r.orderid)} title="Track Order"><TruckIcon size={15} /></button>
        </div>
      ),
      width: "110px", right: true, ignoreRowClick: true, allowOverflow: true, button: true,
    },
  ], []);

  const tableStyles = {
    table: { style: { background: "transparent" } },
    headRow: { style: { background: "#f0fbf6", borderTopLeftRadius: "12px", borderTopRightRadius: "12px", borderBottom: "1px solid #d6efe2", minHeight: "52px" } },
    headCells: { style: { fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#5b6b66", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
      style: { minHeight: "72px", fontSize: "14px", color: "#1f2a37", borderBottom: "1px solid #eef4f1 !important", transition: "background 0.18s ease" },
      highlightOnHoverStyle: { background: "#f6fcf9", borderBottomColor: "#e3f0e9", outline: "none" },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  };

  /* ── Helpers for modal ── */
  const getSubtotal = (order) =>
    Object.values(order.order_products || {}).reduce((sum, p) => {
      return sum + (p.price - (p.price * p.dprice) / 100) * p.qty;
    }, 0);

  return (
    <>
      <style>{`
        :root {
          --ol-bg:#f3faf6;--ol-card:#ffffff;--ol-border:#e3f0e9;--ol-text:#0f1f1a;
          --ol-muted:#6b7a74;--ol-primary:#10b981;--ol-primary-d:#059669;
          --ol-primary-grad:linear-gradient(135deg,#14b8a6 0%,#10b981 100%);
          --ol-soft:#ecfdf5;--ol-edit:#e8f4ff;--ol-edit-fg:#2563eb;
          --ol-track:#fff1e6;--ol-track-fg:#ea7a3b;--ol-del:#fee4e2;--ol-del-fg:#e0413c;
          --ol-shadow:0 1px 2px rgba(16,30,24,.04),0 4px 14px rgba(16,30,24,.04);
          --ol-shadow-lg:0 8px 32px rgba(16,30,24,.1);
        }
        .ol-shell{background:var(--ol-bg);min-height:100%;padding:24px;}
        .ol-head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:20px;}
        .ol-head h2{font-size:clamp(22px,2.4vw,30px);font-weight:800;color:var(--ol-text);margin:0 0 4px 0;letter-spacing:-0.01em;}
        .ol-head p{color:var(--ol-muted);font-size:14px;margin:0;}
        .ol-card{background:var(--ol-card);border:1px solid var(--ol-border);border-radius:18px;box-shadow:var(--ol-shadow);overflow:hidden;}
        .ol-card-top{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:18px 18px 14px;flex-wrap:wrap;border-bottom:1px solid #f0f6f3;}
        .ol-card-top-left{display:flex;align-items:center;gap:10px;flex:1 1 320px;flex-wrap:wrap;}
        .ol-search-box{position:relative;flex:1 1 240px;max-width:380px;}
        .ol-search-box svg{position:absolute;left:14px;top:50%;transform:translateY(-50%);color:#9aa8a2;pointer-events:none;fill:none!important;}
        .ol-search-box input{width:100%;padding:11px 14px 11px 42px;border-radius:999px;border:1px solid var(--ol-border);background:#f6fbf8;font-size:14px;color:var(--ol-text);outline:none;transition:border-color .15s,box-shadow .15s,background .15s;}
        .ol-search-box input::placeholder{color:#9aa8a2;}
        .ol-search-box input:focus{border-color:var(--ol-primary);background:#fff;box-shadow:0 0 0 4px rgba(16,185,129,.12);}
        .ol-total-badge{display:inline-flex;align-items:center;gap:8px;padding:9px 14px;background:#fff;border:1px solid var(--ol-border);border-radius:999px;font-size:13px;color:var(--ol-text);font-weight:600;white-space:nowrap;}
        .ol-total-badge svg{fill:none!important;color:var(--ol-primary);}
        .ol-total-badge b{color:var(--ol-primary-d);}
        .ol-multiselect{flex:1 1 180px;max-width:260px;z-index:20;}
        .ol-multiselect__trigger{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:9px 12px 9px 14px;background:#f6fbf8;border:1px solid var(--ol-border);border-radius:10px;cursor:pointer;font-size:13.5px;color:var(--ol-text);user-select:none;transition:border-color .15s,box-shadow .15s,background .15s;min-height:42px;}
        .ol-multiselect__trigger:hover,.ol-multiselect__trigger:focus{border-color:var(--ol-primary);background:#fff;box-shadow:0 0 0 4px rgba(16,185,129,.12);outline:none;}
        .ol-multiselect__label{display:flex;gap:5px;align-items:center;flex-wrap:wrap;flex:1;}
        .ol-multiselect__tag{display:inline-flex;align-items:center;background:var(--ol-soft);color:#047857;border:1px solid #b7e8d2;border-radius:999px;padding:2px 9px;font-size:12px;font-weight:600;}
        .ol-multiselect__caret{display:inline-flex;align-items:center;gap:4px;color:#9aa8a2;}
        .ol-multiselect__clear{display:inline-flex;align-items:center;justify-content:center;background:none;border:none;cursor:pointer;color:#9aa8a2;padding:2px;transition:color .15s;}
        .ol-multiselect__clear:hover{color:var(--ol-del-fg);}
        .ol-multiselect__clear svg{fill:none!important;}
        .ol-multiselect__dropdown{position:absolute;top:calc(100% + 6px);left:0;right:0;background:#fff;border:1px solid var(--ol-border);border-radius:12px;box-shadow:0 12px 32px rgba(16,30,24,.12);overflow:hidden;animation:olDropIn .18s ease;z-index:100;}
        @keyframes olDropIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        .ol-multiselect__option{display:flex;align-items:center;gap:10px;padding:10px 14px;cursor:pointer;font-size:13.5px;color:var(--ol-text);transition:background .12s;}
        .ol-multiselect__option:hover{background:#f6fbf8;}
        .ol-multiselect__option.is-selected{background:var(--ol-soft);}
        .ol-multiselect__check{width:18px;height:18px;border-radius:5px;border:1.5px solid #c7d3ce;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;background:#fff;transition:all .12s;}
        .ol-multiselect__option.is-selected .ol-multiselect__check{background:var(--ol-primary);border-color:var(--ol-primary);}
        .ol-multiselect__check svg{fill:none!important;color:#fff;}
        .ol-orderid-pill{display:inline-block;padding:4px 10px;background:#eef4f1;color:#4b5b56;border-radius:999px;font-size:12px;font-weight:600;letter-spacing:.02em;font-family:"SFMono-Regular","Consolas",monospace;}
        .ol-customer-cell{display:flex;flex-direction:column;gap:2px;}
        .ol-customer-name{font-weight:600;color:var(--ol-text);font-size:14px;}
        .ol-customer-email{font-size:12px;color:var(--ol-muted);}
        .ol-cell-muted{color:var(--ol-muted);font-size:13px;}
        .ol-amount-cell{font-weight:700;color:var(--ol-text);font-size:14px;}
        .ol-status-pill{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:999px;font-size:12.5px;font-weight:600;border:1px solid transparent;user-select:none;}
        .ol-status-pill .dot{width:7px;height:7px;border-radius:50%;display:inline-block;}
        .ol-status-pill.is-paid{background:#ecfdf5;color:#047857;border-color:#b7e8d2;}
        .ol-status-pill.is-paid .dot{background:#10b981;}
        .ol-status-pill.is-unpaid{background:#fff1f0;color:#c0392b;border-color:#fbd7d3;}
        .ol-status-pill.is-unpaid .dot{background:#e0413c;}
        .ol-status-pill.is-other{background:#f4f4f5;color:#52525b;border-color:#d4d4d8;}
        .ol-status-pill.is-other .dot{background:#a1a1aa;}
        .ol-row-actions{display:inline-flex;gap:6px;}
        .ol-ico-btn{width:34px;height:34px;display:inline-flex;align-items:center;justify-content:center;border-radius:9px;border:none;cursor:pointer;transition:transform .15s,filter .15s;}
        .ol-ico-btn svg{fill:none!important;}
        .ol-ico-btn:hover{transform:translateY(-1px);filter:brightness(.95);}
        .ol-ico-btn.view{background:var(--ol-edit);color:var(--ol-edit-fg);}
        .ol-ico-btn.track{background:var(--ol-track);color:var(--ol-track-fg);}
        .rdt_TableHead input[type="checkbox"],.rdt_TableBody input[type="checkbox"]{appearance:none;-webkit-appearance:none;width:18px;height:18px;border:1.5px solid #c7d3ce;border-radius:5px;background:#fff;cursor:pointer;position:relative;transition:all .15s ease;margin:0;}
        .rdt_TableHead input[type="checkbox"]:checked,.rdt_TableBody input[type="checkbox"]:checked{background:var(--ol-primary);border-color:var(--ol-primary);}
        .rdt_TableHead input[type="checkbox"]:checked::after,.rdt_TableBody input[type="checkbox"]:checked::after{content:"";position:absolute;left:4px;top:1px;width:6px;height:10px;border:solid #fff;border-width:0 2px 2px 0;transform:rotate(45deg);}
        .ol-pagination{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;padding:14px 18px;border-top:1px solid #eef4f1;font-size:13px;color:var(--ol-muted);}
        .ol-pagination__info{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
        .ol-pagination__info b{color:var(--ol-text);}
        .ol-pagination__perpage{padding:6px 10px;border-radius:8px;border:1px solid var(--ol-border);background:#f6fbf8;font-size:13px;color:var(--ol-text);cursor:pointer;outline:none;}
        .ol-pagination__perpage:focus{border-color:var(--ol-primary);}
        .ol-pagination__pages{display:flex;align-items:center;gap:4px;}
        .ol-pagination__btn{display:inline-flex;align-items:center;justify-content:center;min-width:34px;height:34px;padding:0 6px;border-radius:8px;border:1px solid var(--ol-border);background:#fff;font-size:13.5px;font-weight:500;color:var(--ol-text);cursor:pointer;transition:all .15s ease;}
        .ol-pagination__btn svg{fill:none!important;}
        .ol-pagination__btn:hover:not(:disabled){border-color:var(--ol-primary);color:var(--ol-primary);background:var(--ol-soft);}
        .ol-pagination__btn.is-active{background:var(--grad-main);color:#fff;border-color:transparent;box-shadow:0 4px 12px rgba(16,185,129,.25);font-weight:700;}
        .ol-pagination__btn:disabled{opacity:.4;cursor:not-allowed;}
        .ol-pagination__ellipsis{display:inline-flex;align-items:center;justify-content:center;width:30px;color:var(--ol-muted);}
        .ol-overlay{position:fixed;inset:0;z-index:1080;background:rgba(255,255,255,.55);backdrop-filter:blur(2px);display:grid;place-items:center;}
        .ol-spinner{width:42px;height:42px;border-radius:50%;border:3px solid #d6efe2;border-top-color:var(--ol-primary);animation:olSpin 0.8s linear infinite;}
        @keyframes olSpin{to{transform:rotate(360deg)}}
        .ol-empty{padding:48px 20px;text-align:center;color:var(--ol-muted);}
        .ol-empty svg{fill:none!important;color:#c7d3ce;margin-bottom:12px;}
        .ol-empty p{font-size:15px;font-weight:500;margin:0;}
        .ol-empty span{font-size:13px;}
        /* Modal */
        .ol-modal-overlay{position:fixed;inset:0;z-index:1050;background:rgba(10,20,15,.45);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px;animation:olFadeIn .2s ease;overflow-y:auto;}
        @keyframes olFadeIn{from{opacity:0}to{opacity:1}}
        .ol-modal{background:var(--ol-card);border-radius:20px;box-shadow:0 30px 60px rgba(16,30,24,.2),0 0 0 1px rgba(16,185,129,.08);width:100%;max-width:760px;overflow:hidden;animation:olSlideUp .25s cubic-bezier(0.34,1.56,0.64,1);max-height:calc(100vh - 32px);display:flex;flex-direction:column;}
        @keyframes olSlideUp{from{opacity:0;transform:translateY(28px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
        .ol-modal__header{background:linear-gradient(135deg,#ecfdf5 0%,#f0fbf6 100%);border-bottom:1px solid var(--ol-border);padding:20px 24px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-shrink:0;}
        .ol-modal__header-left{display:flex;align-items:center;gap:12px;}
        .ol-modal__icon{width:44px;height:44px;background:var(--grad-main);border-radius:12px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(16,185,129,.3);flex-shrink:0;}
        .ol-modal__icon svg{fill:none!important;color:#fff;}
        .ol-modal__title{font-size:18px;font-weight:700;color:var(--ol-text);margin:0;}
        .ol-modal__subtitle{font-size:13px;color:var(--ol-muted);margin:2px 0 0;}
        .ol-modal__header-right{display:flex;align-items:center;gap:8px;}
        .ol-modal__close{width:36px;height:36px;display:inline-flex;align-items:center;justify-content:center;background:#fff;border:1px solid var(--ol-border);border-radius:9px;cursor:pointer;color:var(--ol-muted);transition:all .15s;}
        .ol-modal__close svg{fill:none!important;}
        .ol-modal__close:hover{background:var(--ol-del);color:var(--ol-del-fg);border-color:#fbcfcd;}
        .ol-pdf-btn{display:inline-flex;align-items:center;gap:6px;padding:8px 14px;border-radius:10px;background:var(--grad-main);color:#fff;border:none;font-size:13px;font-weight:600;cursor:pointer;box-shadow:0 4px 12px rgba(16,185,129,.22);transition:filter .15s,box-shadow .15s,transform .15s;white-space:nowrap;}
        .ol-pdf-btn svg{fill:none!important;}
        .ol-pdf-btn:hover{filter:brightness(1.07);transform:translateY(-1px);box-shadow:0 6px 18px rgba(16,185,129,.32);}
        .ol-modal__body{padding:24px;overflow-y:auto;flex:1;}
        .ol-info-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px;margin-bottom:24px;}
        .ol-info-card{background:#f6fbf8;border:1px solid var(--ol-border);border-radius:12px;padding:14px 16px;display:flex;gap:12px;align-items:flex-start;}
        .ol-info-card__icon{width:36px;height:36px;background:var(--grad-main);border-radius:9px;display:flex;align-items:center;justify-content:center;flex-shrink:0;box-shadow:0 3px 8px rgba(16,185,129,.2);}
        .ol-info-card__icon svg{fill:none!important;color:#fff;}
        .ol-info-card__content{flex:1;min-width:0;}
        .ol-info-card__label{font-size:11px;font-weight:700;color:var(--ol-muted);text-transform:uppercase;letter-spacing:.07em;margin-bottom:4px;}
        .ol-info-card__value{font-size:14px;font-weight:600;color:var(--ol-text);line-height:1.4;word-break:break-word;}
        .ol-info-card__value.is-email{font-size:13px;font-weight:500;color:var(--ol-muted);}
        .ol-section-title{font-size:13px;font-weight:700;color:var(--ol-muted);text-transform:uppercase;letter-spacing:.07em;margin:0 0 12px 0;display:flex;align-items:center;gap:8px;}
        .ol-section-title::after{content:"";flex:1;height:1px;background:var(--ol-border);}
        .ol-products-table{width:100%;border-collapse:separate;border-spacing:0;border:1px solid var(--ol-border);border-radius:12px;overflow:hidden;margin-bottom:20px;}
        .ol-products-table thead tr{background:#f0fbf6;}
        .ol-products-table th{padding:12px 14px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#5b6b66;border-bottom:1px solid #d6efe2;text-align:left;white-space:nowrap;}
        .ol-products-table th:last-child{text-align:right;}
        .ol-products-table td{padding:14px;font-size:14px;border-bottom:1px solid #eef4f1;vertical-align:middle;color:var(--ol-text);}
        .ol-products-table tbody tr:last-child td{border-bottom:none;}
        .ol-products-table td:last-child{text-align:right;font-weight:600;}
        .ol-products-table tbody tr{transition:background .15s;}
        .ol-products-table tbody tr:hover{background:#f6fcf9;}
        .ol-product-thumb{width:52px;height:52px;object-fit:cover;border-radius:10px;border:1px solid var(--ol-border);background:#f6fbf8;}
        .ol-product-info{display:flex;align-items:center;gap:12px;}
        .ol-product-name{font-weight:600;color:var(--ol-text);}
        .ol-discount-badge{display:inline-block;padding:2px 8px;background:#fff7ed;color:#c2410c;border-radius:999px;font-size:12px;font-weight:600;}
        .ol-total-summary{display:flex;justify-content:flex-end;}
        .ol-total-box{background:#f0fbf6;border:1px solid #d6efe2;border-radius:14px;padding:18px 24px;min-width:280px;}
        .ol-total-line{display:flex;justify-content:space-between;align-items:center;font-size:14px;color:var(--ol-muted);padding:5px 0;}
        .ol-total-line.is-grand{font-size:16px;font-weight:700;color:var(--ol-text);border-top:1px solid #d6efe2;margin-top:8px;padding-top:12px;}
        .ol-total-line.is-grand span:last-child{color:var(--ol-primary-d);}
        .ol-total-line.is-coupon{color:#16a34a;font-weight:600;}
        .ol-modal__footer{padding:16px 24px;border-top:1px solid var(--ol-border);background:#fafefb;display:flex;align-items:center;justify-content:flex-end;gap:10px;flex-shrink:0;}
        .ol-btn-close-modal{padding:10px 18px;border-radius:10px;background:#fff;border:1px solid var(--ol-border);font-size:14px;font-weight:500;color:var(--ol-muted);cursor:pointer;transition:all .15s;}
        .ol-btn-close-modal:hover{background:#f6fbf8;color:var(--ol-text);}
        @media(max-width:768px){.ol-shell{padding:14px;}.ol-head,.ol-card-top{flex-direction:column;align-items:stretch;}.ol-search-box,.ol-multiselect{max-width:100%;}.ol-pagination{flex-direction:column;align-items:center;gap:10px;}.ol-modal{border-radius:16px;}.ol-info-grid{grid-template-columns:1fr 1fr;}}
        @media(max-width:480px){.ol-info-grid{grid-template-columns:1fr;}.ol-products-table{font-size:12px;}.ol-products-table th,.ol-products-table td{padding:10px;}}
        .ol-shell svg,.ol-modal-overlay svg{fill:none;}
      `}</style>

      <div id="layout-wrapper">
        <Header />
        {loading && <div className="ol-overlay"><div className="ol-spinner" /></div>}

        <div className="main-content">
          <div className="page-content">
            <div className="ol-shell">

              <div className="ol-head">
                <div>
                  <h2>Orders</h2>
                  <p>View and manage all customer orders in one place.</p>
                </div>
              </div>

              <div className="ol-card">
                <div className="ol-card-top">
                  <div className="ol-card-top-left">
                    <div className="ol-search-box">
                      <SearchIcon size={17} />
                      <input type="text" placeholder="Search by order ID, customer…"
                        value={filterText} onChange={e => setFilterText(e.target.value)} />
                    </div>
                    <MultiSelect options={["Paid", "Un Paid"]} value={statusFilter}
                      onChange={setStatusFilter} placeholder="Filter by status…" />
                  </div>
                  <div className="ol-total-badge">
                    <ShoppingBagIcon size={16} />
                    Total:&nbsp;<b>{totalRows}</b>
                  </div>
                </div>

                <DataTable
                  columns={columns}
                  data={filteredOrders}
                  customStyles={tableStyles}
                  highlightOnHover responsive striped
                  noDataComponent={
                    <div className="ol-empty">
                      <ShoppingBagIcon size={40} />
                      <p>No orders found</p>
                      <span>Try adjusting your search or filters</span>
                    </div>
                  }
                />

                {totalRows > 0 && (
                  <CustomPagination
                    currentPage={currentPage} totalPages={totalPages}
                    onPageChange={handlePageChange} perPage={perPage}
                    totalRows={totalRows} onPerPageChange={handlePerPageChange}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ── Order Detail Modal ── */}
          {showModal && selectedOrder && (
            <div className="ol-modal-overlay"
              onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
              aria-modal="true" role="dialog">
              <div className="ol-modal">

                {/* Header */}
                <div className="ol-modal__header">
                  <div className="ol-modal__header-left">
                    <div className="ol-modal__icon"><ReceiptIcon size={22} /></div>
                    <div>
                      <p className="ol-modal__title">Order Details</p>
                      <p className="ol-modal__subtitle">#{selectedOrder.orderid} · {selectedOrder.orderdate}</p>
                    </div>
                  </div>
                  <div className="ol-modal__header-right">
                    <button className="ol-pdf-btn" onClick={() => downloadOrderPDF(selectedOrder)}>
                      <DownloadIcon size={15} /> Download PDF
                    </button>
                    <button className="ol-modal__close" onClick={handleClose}><CloseIcon size={16} /></button>
                  </div>
                </div>

                {/* Body */}
                <div className="ol-modal__body">

                  {/* Info Cards */}
                  <div className="ol-info-grid">
                    <div className="ol-info-card">
                      <div className="ol-info-card__icon"><UserIcon size={18} /></div>
                      <div className="ol-info-card__content">
                        <div className="ol-info-card__label">Customer</div>
                        <div className="ol-info-card__value">{selectedOrder.name}</div>
                        <div className="ol-info-card__value is-email">{selectedOrder.email}</div>
                      </div>
                    </div>
                    <div className="ol-info-card">
                      <div className="ol-info-card__icon"><MapPinIcon size={18} /></div>
                      <div className="ol-info-card__content">
                        <div className="ol-info-card__label">Shipping Address</div>
                        <div className="ol-info-card__value">
                          {[selectedOrder.address, selectedOrder.city, selectedOrder.state, selectedOrder.country].filter(Boolean).join(", ")}
                        </div>
                      </div>
                    </div>
                    <div className="ol-info-card">
                      <div className="ol-info-card__icon"
                        style={{ background: selectedOrder.status === "Paid" ? "#2F4A3A" : "#2F4A3A" }}>
                        <ReceiptIcon size={18} />
                      </div>
                      <div className="ol-info-card__content">
                        <div className="ol-info-card__label">Payment Status</div>
                        <span className={`ol-status-pill ${selectedOrder.status === "Paid" ? "is-paid" : selectedOrder.status === "Un Paid" ? "is-unpaid" : "is-other"}`}
                          style={{ marginTop: 4 }}>
                          <span className="dot" />{selectedOrder.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Products */}
                  <p className="ol-section-title"><ShoppingBagIcon size={14} /> Order Items</p>
                  <div style={{ overflowX: "auto" }}>
                    <table className="ol-products-table">
                      <thead>
                        <tr>
                          <th>Product</th><th>Unit Price</th><th>Discount</th><th>Qty</th><th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(selectedOrder.order_products).map(key => {
                          const p = selectedOrder.order_products[key];
                          const lineTotal = (p.price - (p.price * p.dprice) / 100) * p.qty;
                          return (
                            <tr key={key}>
                              <td>
                                <div className="ol-product-info">
                                  <img src={p.image} alt={p.name} className="ol-product-thumb" />
                                  <span className="ol-product-name">{p.name}</span>
                                </div>
                              </td>
                              <td className="ol-cell-muted">₹{p.price}</td>
                              <td><span className="ol-discount-badge">{p.dprice}%</span></td>
                              <td style={{ fontWeight: 600 }}>{p.qty}</td>
                              <td>₹{lineTotal.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* ── Bill Summary with Coupon ── */}
                  <div className="ol-total-summary">
                    <div className="ol-total-box">
                      <div className="ol-total-line">
                        <span>Subtotal (MRP)</span>
                        <span style={{ fontWeight: 600, color: "var(--ol-text)" }}>
                          ₹{getSubtotal(selectedOrder).toFixed(2)}
                        </span>
                      </div>
                      <div className="ol-total-line">
                        <span>Delivery Charges</span>
                        {Number(selectedOrder.shipping_amount) === 0
                          ? <span style={{ color: "#16a34a", fontWeight: 700 }}>FREE</span>
                          : <span style={{ fontWeight: 600, color: "var(--ol-text)" }}>
                              ₹{Number(selectedOrder.shipping_amount).toFixed(2)}
                            </span>
                        }
                      </div>
                      {Number(selectedOrder.coupen_amount) > 0 && (
                        <div className="ol-total-line is-coupon">
                          <span>
                            Coupon Discount
                            {selectedOrder.coupon_code && (
                              <span style={{
                                marginLeft: 6, fontSize: 11, fontWeight: 700,
                                background: "#ecfdf5", color: "#047857",
                                border: "1px solid #b7e8d2",
                                borderRadius: 999, padding: "2px 8px",
                              }}>
                                {selectedOrder.coupon_code}
                              </span>
                            )}
                          </span>
                          <span>- ₹{Number(selectedOrder.coupen_amount).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="ol-total-line is-grand">
                        <span>Amount Paid</span>
                        <span>
                          ₹{selectedOrder.final_amount
                            ? Number(selectedOrder.final_amount).toFixed(2)
                            : Number(selectedOrder.totalamount).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="ol-modal__footer">
                  <button className="ol-btn-close-modal" onClick={handleClose}>Close</button>
                  <button className="ol-pdf-btn" onClick={() => downloadOrderPDF(selectedOrder)}>
                    <DownloadIcon size={15} /> Download PDF
                  </button>
                </div>
              </div>
            </div>
          )}

          <Footer />
        </div>
      </div>
    </>
  );
};

export default OrderList;